import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Button from 'components/Button'
import BackArrow from 'components/icons/BackArrow'
import CaretRight from 'components/icons/CaretRight'
import CollegeCard from 'components/CollegeCard'
import StudentsTable from '../StudentsTable'
import { debounce } from 'lodash'

import {
  SectionWrapper,
  TopHeader,
  TopSection,
  ButtonContainer,
  ContentSection,
  FormSection,
  SubHeading,
  FormRow,
  FormGroup,
  StyledLabel,
  Select,
  StatusContainer,
  StatusItem,
  HelperText,
  HeaderContainer,
  AssessmentInfoContainer,
  FilterSummaryBar,
  SummaryText,
  ClearFilterButton,
} from './styles'
import {
  setChartFilters as setChartFiltersAction,
  clearChartFilters as clearChartFiltersAction,
  fetchSpecificAssessmentStudentList, // Import new action
  fetchAssessmentDetails as fetchAssessmentDetailsAction,
  // Assessment-specific filter actions
  getAssessmentDomains,
  getAssessmentDegrees,
  getAssessmentDepartments,
  getAssessmentSpecializations,
} from '../../actions'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectSpecificAssessmentStudentList,
  selectSpecificAssessmentStudentListLoading,
  selectSpecificAssessmentStudentListPagination,
} from '../../selectors'
import CandidateList from '../CandidateList'
import StudentReportModal from '../StudentReport'
import AssessmentCharts from '../../components/AssessmentCharts'
import AssessmentNavBar from '../../components/AssessmentNavBar'


const AssessmentDetails = ({
  assessment,
  onClose,
  loading,
  entityType,

  // NEW: Filter props from parent (will be overridden by API below if available)
  domainOptions,
  degreeOptions,
  departmentOptions,
  specializationOptions,
  topLevelDomain,
  setTopLevelDomain,
  topLevelDegree,
  setTopLevelDegree,
  topLevelDepartment,
  setTopLevelDepartment,
  topLevelSpecialization,
  setTopLevelSpecialization,
  topLevelPassingYear,
  setTopLevelPassingYear,
  searchTerm,
  setSearchTerm,



  // Actions
  fetchStudentsByStatus,
  fetchAllStudentStatusCounts,
  exportStudentData,
  sendRemindersToStudents,
  resendInvitesToStudents,
  instituteDetails,
  onSwitchToDiagnosis,
}) => {
  const [studentForReport, setStudentForReport] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const [currentActiveStatus, setCurrentActiveStatus] = useState('sent')
  // NEW: Sort state for server-side sorting
  const [sortBy, setSortBy] = useState({ field: 'name', order: 'asc' })
  // Status filter (multi-select) for non-diagnosis assessments
  const [statusFilter, setStatusFilter] = useState([])

  const getInitialNavIndex = () => {
    if (assessment?.isDiagnosis) return 0
    const details = assessment?.assessmentDetails || []
    const normalizedType = (assessment?.assessmentType || '').toLowerCase()
    const hasDiagnosis = ['communication', 'aptitude', 'hinglish'].includes(normalizedType)
    if (!hasDiagnosis || !details.length) return 0
    const clickedMapId = assessment?.assessmentInstituteMapId || assessment?.id
    const detailIdx = details.findIndex(d =>
      d.assessmentInstituteMapId === clickedMapId || d.auditId === clickedMapId
    )
    return detailIdx >= 0 ? detailIdx + 1 : 1
  }
  const [currentNavIndex, setCurrentNavIndex] = useState(getInitialNavIndex)

  const navList = useMemo(() => {
    const details = assessment?.assessmentDetails || []
    if (!details.length) return []
    const normalizedType = (assessment?.assessmentType || '').toLowerCase()
    const hasDiagnosis = ['communication', 'aptitude', 'hinglish'].includes(normalizedType)
    if (hasDiagnosis) {
      const diagEntry = {
        isDiagnosis: true,
        title: 'Diagnosis',
        status: assessment.diagnosisStatus || 'Completed',
        startDate: details[0]?.startDate || details[0]?.startTime,
      }
      return [diagEntry, ...details]
    }
    return details
  }, [assessment])

  // Derive the active assessment from nav index
  const activeAssessment = useMemo(() => {
    if (navList.length > 1) {
      return navList[currentNavIndex] || assessment
    }
    return assessment
  }, [navList, currentNavIndex, assessment])

  const isDiagnosisActive = activeAssessment?.isDiagnosis === true

  const chartAssessment = useMemo(() => {
    if (!activeAssessment || activeAssessment === assessment) return assessment
    return {
      ...assessment,
      ...activeAssessment,
      assessmentType: assessment?.assessmentType,
      scheduleId: assessment?.scheduleId,
      scheduleName: assessment?.scheduleName,
      assessmentDetails: assessment?.assessmentDetails,
    }
  }, [activeAssessment, assessment])

  // Determine if this is a "standard" type (communication/aptitude) or "other" type
  const assessmentTypeLower = (assessment?.assessmentType || '').toLowerCase()
  const isStandardType = ['communication', 'aptitude', 'hinglish'].includes(assessmentTypeLower)
  const isOtherType = !isStandardType && !!assessment

  // Redux state
  const dispatch = useDispatch()
  const chartFiltersFromRedux = useSelector(state => state.SubscribedInstitutes?.chartFilters)

  const studentList = useSelector(selectSpecificAssessmentStudentList)
  const studentListLoading = useSelector(selectSpecificAssessmentStudentListLoading)
  const studentListPagination = useSelector(selectSpecificAssessmentStudentListPagination)

  // For other types: use Redux studentData from fetchAssessmentDetails
  const otherTypeStudentData = useSelector(state => state.SubscribedInstitutes?.studentData)
  const otherTypeDataLoading = useSelector(state => state.SubscribedInstitutes?.studentDataLoading)
  const otherTypeDataPagination = useSelector(state => state.SubscribedInstitutes?.studentDataPagination)

  // Enhanced debugging
  console.log('🔍 [AssessmentDetails] REDUX DEBUG:')
  console.log('  - studentList:', studentList)
  console.log('  - studentList length:', studentList?.length)
  console.log('  - studentListLoading:', studentListLoading)
  console.log('  - chartFiltersFromRedux:', chartFiltersFromRedux)


  // Safely derive the correct assessment identifier expected by the API
  const selectedAssessmentId = useMemo(() => {
    if (!activeAssessment) return null
    if (activeAssessment.isDiagnosis) {
      return assessment?.scheduleId || null
    }
    return (
      activeAssessment.assessmentInstituteMapID ||
      activeAssessment.assessment_institute_map_id ||
      activeAssessment.assessmentInstituteMapId ||
      activeAssessment.id ||
      activeAssessment.latestAssessmentInstituteMapId ||
      activeAssessment.scheduleId ||
      null
    )
  }, [activeAssessment, assessment])

  // ------------------------------
  // New hierarchical options (Domain -> Degree -> Department -> Specialization)
  // Fetched using assessmentInstituteMapId provided by the user API
  // ------------------------------
  const [domainTree, setDomainTree] = useState([])
  const [domainOptionsLocal, setDomainOptionsLocal] = useState([])
  const [degreeOptionsLocal, setDegreeOptionsLocal] = useState([])
  const [departmentOptionsLocal, setDepartmentOptionsLocal] = useState([])
  const [specializationOptionsLocal, setSpecializationOptionsLocal] = useState([])

  // Fetch the full domain-degree-department-specialization tree once per assessment
  // useEffect(() => {
  //   const fetchDomainsTree = async () => {
  //     if (!selectedAssessmentId) return
  //     try {
  //       const url = `http://127.0.0.1:2009/institutes/studentListInfo/domains?assessmentInstituteMapId=${encodeURIComponent(selectedAssessmentId)}`
  //       const res = await fetch(url)
  //       if (!res.ok) throw new Error(`HTTP ${res.status}`)
  //       const json = await res.json()
  //       const tree = Array.isArray(json?.data) ? json.data : []
  //       setDomainTree(tree)
  //       // Initialize domain options
  //       const domOpts = tree.map(d => ({ label: d.domainName, value: d.domainId }))
  //       setDomainOptionsLocal(domOpts)
  //     } catch (e) {
  //       console.error('[AssessmentDetails] Failed to fetch domain tree:', e)
  //       setDomainTree([])
  //       setDomainOptionsLocal([])
  //     }
  //   }
  //   fetchDomainsTree()
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedAssessmentId])

  // Re-compute degree options when top-level domain changes
  useEffect(() => {
    if (!domainTree || domainTree.length === 0) {
      setDegreeOptionsLocal([])
      return
    }
    const selectedDomainIds = (topLevelDomain || []).map(d => d.value)
    const degrees = []
    domainTree.forEach(d => {
      if (selectedDomainIds.length === 0 || selectedDomainIds.includes(d.domainId)) {
        (d.degrees || []).forEach(deg => {
          degrees.push({ label: deg.degreeName, value: deg.degreeId })
        })
      }
    })
    // Deduplicate by value
    const unique = Array.from(new Map(degrees.map(i => [i.value, i])).values())
    setDegreeOptionsLocal(unique)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainTree, topLevelDomain])

  // Re-compute department options when degree changes
  useEffect(() => {
    if (!domainTree || domainTree.length === 0) {
      setDepartmentOptionsLocal([])
      return
    }
    const selectedDomainIds = (topLevelDomain || []).map(d => d.value)
    const selectedDegreeIds = (topLevelDegree || []).map(d => d.value)
    const departments = []
    domainTree.forEach(dom => {
      if (selectedDomainIds.length === 0 || selectedDomainIds.includes(dom.domainId)) {
        (dom.degrees || []).forEach(deg => {
          if (selectedDegreeIds.length === 0 || selectedDegreeIds.includes(deg.degreeId)) {
            (deg.departments || []).forEach(dep => {
              departments.push({ label: dep.departmentName, value: `${deg.degreeId}::${dep.departmentName}` })
            })
          }
        })
      }
    })
    // Deduplicate by label
    const unique = Array.from(new Map(departments.map(i => [i.label, i])).values())
    setDepartmentOptionsLocal(unique)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainTree, topLevelDomain, topLevelDegree])

  // Re-compute specialization options when department changes
  useEffect(() => {
    if (!domainTree || domainTree.length === 0) {
      setSpecializationOptionsLocal([])
      return
    }
    const selectedDomainIds = (topLevelDomain || []).map(d => d.value)
    const selectedDegreeIds = (topLevelDegree || []).map(d => d.value)
    const selectedDepartmentLabels = (topLevelDepartment || []).map(d => d.label)
    const specs = []
    domainTree.forEach(dom => {
      if (selectedDomainIds.length === 0 || selectedDomainIds.includes(dom.domainId)) {
        (dom.degrees || []).forEach(deg => {
          if (selectedDegreeIds.length === 0 || selectedDegreeIds.includes(deg.degreeId)) {
            (deg.departments || []).forEach(dep => {
              if (selectedDepartmentLabels.length === 0 || selectedDepartmentLabels.includes(dep.departmentName)) {
                (dep.streams || []).forEach(stream => {
                  (stream.specializations || []).forEach(sp => {
                    const name = sp.specializationName && sp.specializationName.trim() !== '' ? sp.specializationName : '-' // some responses have empty strings
                    // Build a stable value using available identifiers
                    const valueKey = `${stream.streamId || 'stream'}::${sp.specializationId || 'none'}::${sp.instituteCampusId || ''}`
                    specs.push({ label: name, value: valueKey })
                  })
                })
              }
            })
          }
        })
      }
    })
    // Deduplicate by label+value for safety
    const unique = Array.from(new Map(specs.map(i => [`${i.label}::${i.value}`, i])).values())
    setSpecializationOptionsLocal(unique)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainTree, topLevelDomain, topLevelDegree, topLevelDepartment])

  // Chart filter functions
  const setChartFilters = (filters) => {
    console.log('🔧 [AssessmentDetails] setChartFilters called with:', filters)
    dispatch(setChartFiltersAction(filters))
  }

  const clearChartFilters = () => {
    console.log('🔧 [AssessmentDetails] clearChartFilters called')
    dispatch(clearChartFiltersAction())
  }

  // Handler for back button - clears all filters before navigating back
  const handleBackClick = () => {
    console.log('🔧 [AssessmentDetails] handleBackClick - clearing all filters')
    // Clear chart filters (CEFR, Aptitude, Consistency)
    dispatch(clearChartFiltersAction())
    // Clear top-level filters
    if (setTopLevelDomain) setTopLevelDomain([])
    if (setTopLevelDegree) setTopLevelDegree([])
    if (setTopLevelDepartment) setTopLevelDepartment([])
    if (setTopLevelSpecialization) setTopLevelSpecialization([])
    if (setTopLevelPassingYear) setTopLevelPassingYear(null)
    if (setSearchTerm) setSearchTerm('')
    // Navigate back
    if (onClose) onClose()
  }

  // ====================================================================
  // LOCAL STATE FOR ASSESSMENT-SPECIFIC FILTER OPTIONS
  // These are fetched from the new APIs based on assessment context
  // ====================================================================
  const [localDomainOptions, setLocalDomainOptions] = useState([])
  const [localDegreeOptions, setLocalDegreeOptions] = useState([])
  const [localDepartmentOptions, setLocalDepartmentOptions] = useState([])
  const [localSpecializationOptions, setLocalSpecializationOptions] = useState([])

  // Fetch domains when assessment is selected
  // If domains is empty, auto-fetch degrees (and so on)
  // Using scheduleId to match DiagnosisList pattern
  useEffect(() => {
    const fetchDomains = async () => {
      // One-time / standalone assessments carry scheduleId = the map id (a bogus
      // "schedule" id from getschedulesInfo). The options API prefers its scheduleId
      // branch and would look up a non-existent schedule → empty "No data" dropdowns.
      // Suppress it so the real assessmentId branch is used.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (!scheduleId && !instituteDetails?.instituteId) return

      const domains = await dispatch(getAssessmentDomains({
        scheduleId: scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteDetails?.instituteId || '',
      }))

      if (domains && domains.length > 0) {
        setLocalDomainOptions(domains)
      } else {
        // Domains is empty, auto-fetch degrees
        setLocalDomainOptions([])
        const degrees = await dispatch(getAssessmentDegrees({
          scheduleId: scheduleId,
          assessmentId: selectedAssessmentId || '',
          passingYear: topLevelPassingYear || '',
          instituteId: instituteDetails?.instituteId || '',
          domains: [], // Empty array to fetch all
        }))

        if (degrees && degrees.length > 0) {
          setLocalDegreeOptions(degrees)
        } else {
          // Degrees is also empty, auto-fetch departments
          setLocalDegreeOptions([])
          const departments = await dispatch(getAssessmentDepartments({
            scheduleId: scheduleId,
            assessmentId: selectedAssessmentId || '',
            passingYear: topLevelPassingYear || '',
            instituteId: instituteDetails?.instituteId || '',
            degrees: [], // Empty array to fetch all
          }))

          if (departments && departments.length > 0) {
            setLocalDepartmentOptions(departments)
          } else {
            // Departments is also empty, auto-fetch specializations
            setLocalDepartmentOptions([])
            const specializations = await dispatch(getAssessmentSpecializations({
              scheduleId: scheduleId,
              assessmentId: selectedAssessmentId || '',
              passingYear: topLevelPassingYear || '',
              instituteId: instituteDetails?.instituteId || '',
              departments: [], // Empty array to fetch all
            }))
            setLocalSpecializationOptions(specializations || [])
          }
        }
      }
    }
    fetchDomains()
  }, [assessment?.scheduleId, topLevelPassingYear, instituteDetails?.instituteId, dispatch])

  // Fetch degrees (Independent of Domain) - Matches DiagnosisList logic
  useEffect(() => {
    const fetchDegrees = async () => {
      // One-time / standalone assessments carry scheduleId = the map id (a bogus
      // "schedule" id from getschedulesInfo). The options API prefers its scheduleId
      // branch and would look up a non-existent schedule → empty "No data" dropdowns.
      // Suppress it so the real assessmentId branch is used.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (!scheduleId && !instituteDetails?.instituteId) return

      // Always fetch all degrees regardless of domain selection
      const degrees = await dispatch(getAssessmentDegrees({
        scheduleId: scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteDetails?.instituteId || '',
        domains: [], // Fetch all degrees
      }))

      if (degrees && degrees.length > 0) {
        setLocalDegreeOptions(degrees)
      } else {
        // Degrees is empty, auto-fetch departments
        setLocalDegreeOptions([])
        const departments = await dispatch(getAssessmentDepartments({
          scheduleId: scheduleId,
          assessmentId: selectedAssessmentId || '',
          passingYear: topLevelPassingYear || '',
          instituteId: instituteDetails?.instituteId || '',
          degrees: [], // Empty array to fetch all
        }))

        if (departments && departments.length > 0) {
          setLocalDepartmentOptions(departments)
        } else {
          // Departments is also empty, auto-fetch specializations
          setLocalDepartmentOptions([])
          const specializations = await dispatch(getAssessmentSpecializations({
            scheduleId: scheduleId,
            assessmentId: selectedAssessmentId || '',
            passingYear: topLevelPassingYear || '',
            instituteId: instituteDetails?.instituteId || '',
            departments: [], // Empty array to fetch all
          }))
          setLocalSpecializationOptions(specializations || [])
        }
      }
    }
    fetchDegrees()
  }, [assessment?.scheduleId, topLevelPassingYear, instituteDetails?.instituteId, dispatch])

  // Fetch departments when degree selection changes
  // If departments is empty, auto-fetch specializations
  useEffect(() => {
    const fetchDepartments = async () => {
      // One-time / standalone assessments carry scheduleId = the map id (a bogus
      // "schedule" id from getschedulesInfo). The options API prefers its scheduleId
      // branch and would look up a non-existent schedule → empty "No data" dropdowns.
      // Suppress it so the real assessmentId branch is used.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (topLevelDegree?.length === 0) {
        setLocalDepartmentOptions([])
        return
      }

      const selectedDegreeValues = topLevelDegree
        .map(d => d.label || d.value)
        .filter(val => val !== 'all' && val !== 'All') // Filter out 'Select All' value

      if (selectedDegreeValues.length === 0) {
        setLocalDepartmentOptions([])
        return
      }

      const departments = await dispatch(getAssessmentDepartments({
        scheduleId: scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteDetails?.instituteId || '',
        degrees: selectedDegreeValues,
      }))

      if (departments && departments.length > 0) {
        setLocalDepartmentOptions(departments)
      } else {
        // Departments is empty, auto-fetch specializations
        setLocalDepartmentOptions([])
        const specializations = await dispatch(getAssessmentSpecializations({
          scheduleId: scheduleId,
          assessmentId: selectedAssessmentId || '',
          passingYear: topLevelPassingYear || '',
          instituteId: instituteDetails?.instituteId || '',
          departments: [], // Empty array to fetch all
        }))
        setLocalSpecializationOptions(specializations || [])
      }
    }
    fetchDepartments()
  }, [topLevelDegree, assessment?.scheduleId, topLevelPassingYear, instituteDetails?.instituteId, dispatch])

  // Fetch specializations when department selection changes
  useEffect(() => {
    const fetchSpecializations = async () => {
      // One-time / standalone assessments carry scheduleId = the map id (a bogus
      // "schedule" id from getschedulesInfo). The options API prefers its scheduleId
      // branch and would look up a non-existent schedule → empty "No data" dropdowns.
      // Suppress it so the real assessmentId branch is used.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (topLevelDepartment?.length === 0) {
        setLocalSpecializationOptions([])
        return
      }

      const selectedDeptValues = topLevelDepartment
        .map(d => d.label || d.value)
        .filter(val => val !== 'all' && val !== 'All')

      if (selectedDeptValues.length === 0) {
        setLocalSpecializationOptions([])
        return
      }

      const specializations = await dispatch(getAssessmentSpecializations({
        scheduleId: scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteDetails?.instituteId || '',
        departments: selectedDeptValues,
      }))
      setLocalSpecializationOptions(specializations || [])
    }
    fetchSpecializations()
  }, [topLevelDepartment, assessment?.scheduleId, topLevelPassingYear, instituteDetails?.instituteId, dispatch])

  // Merge local options with props (local takes priority if available)
  const effectiveDomainOptions = localDomainOptions.length > 0 ? localDomainOptions : domainOptions
  const effectiveDegreeOptions = localDegreeOptions.length > 0 ? localDegreeOptions : degreeOptions
  const effectiveDepartmentOptions = localDepartmentOptions.length > 0 ? localDepartmentOptions : departmentOptions
  const effectiveSpecializationOptions = localSpecializationOptions.length > 0 ? localSpecializationOptions : specializationOptions



  // Fetch student list when filters/status/assessment change
  // For standard types (communication/aptitude): use fetchSpecificAssessmentStudentList
  // For other types: use fetchAssessmentDetails (admin-node getAssessmentDetails)
  //
  // Debounced: a single dropdown change cascades through 4+ state setters
  // (e.g. handleDomainChange clears degree/dept/specialization) and Redux
  // selector reference churn, which would otherwise fire the effect multiple
  // times in close succession. The setTimeout + cleanup pattern coalesces the
  // burst into ONE API call without changing any params.
  useEffect(() => {
    if (!selectedAssessmentId) return

    const timer = setTimeout(() => {
      if (isOtherType) {
        // Use admin-node getAssessmentDetails for other types
        const apiParams = {
          assessmentId: selectedAssessmentId,
          entityType: entityType || 'college',
          currentPage: currentPage,
          pageSize: pageSize,
          searchTerm: searchTerm || '',
          filters: {
            degree: topLevelDegree.map(i => i.label || i.value).filter(v => v !== 'all'),
            department: topLevelDepartment.map(i => i.label || i.value).filter(v => v !== 'all'),
            specialization: topLevelSpecialization.map(i => i.label || i.value).filter(v => v !== 'all'),
            status: statusFilter || [],
          }
        }
        dispatch(fetchAssessmentDetailsAction(apiParams))
        return
      }

      // Standard types: use fetchSpecificAssessmentStudentList
      if (typeof fetchSpecificAssessmentStudentList !== 'function') return

      const effectivePageSize = pageSize

      // Build API params matching admin-react (scheduleId XOR assessmentId, no attemptNumber)
      const apiParams = {
        ...(isDiagnosisActive
          ? { scheduleId: assessment?.scheduleId }
          : { assessmentId: selectedAssessmentId }),
        page: currentPage,
        limit: effectivePageSize,
        search: searchTerm || '',
        passingYear: topLevelPassingYear || '',
        // Filter arrays
        domain: topLevelDomain.map(i => i.label || i.value).filter(v => v !== 'all'),
        degree: topLevelDegree.map(i => i.label || i.value).filter(v => v !== 'all'),
        department: topLevelDepartment.map(i => i.label || i.value).filter(v => v !== 'all'),
        specialization: topLevelSpecialization.map(i => i.label || i.value).filter(v => v !== 'all'),
        // Chart filters (CEFR, Aptitude, Consistency)
        Cefr: chartFiltersFromRedux?.cefrLevel ? [chartFiltersFromRedux.cefrLevel] : [],
        AptitudeLevel: chartFiltersFromRedux?.aptitudeLevel ? [chartFiltersFromRedux.aptitudeLevel] : [],
        consistency: chartFiltersFromRedux?.consistency ? [chartFiltersFromRedux.consistency] : [],
        // Status filter (multi-select) only for non-diagnosis
        statusFilter: !isDiagnosisActive ? (statusFilter || []) : [],
        // Sorting - use state value
        sortBy
      }

      console.log('[AssessmentDetails] fetchSpecificAssessmentStudentList params ->', apiParams)

      // Dispatch new action
      if (instituteDetails?.instituteId) {
        dispatch(fetchSpecificAssessmentStudentList(instituteDetails.instituteId, apiParams))
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [
    selectedAssessmentId,
    entityType,
    currentActiveStatus,
    currentPage,
    searchTerm,
    topLevelDomain,
    topLevelDegree,
    topLevelDepartment,
    topLevelSpecialization,
    topLevelPassingYear,
    pageSize,
    chartFiltersFromRedux,
    assessment?.assessmentType,
    fetchSpecificAssessmentStudentList,
    instituteDetails?.instituteId,
    dispatch,
    sortBy, // Added sortBy to trigger re-fetch when sort changes
    statusFilter,
    isOtherType,
    isDiagnosisActive,
    assessment?.scheduleId,
  ])

  // Reset status filter when switching between assessments or into/out of diagnosis
  useEffect(() => {
    setStatusFilter([])
  }, [selectedAssessmentId, isDiagnosisActive])

  const handleStatusClick = status => {
    setCurrentActiveStatus(status)
    setCurrentPage(1) // Reset to first page when changing status
  }

  const handleCandidateClick = student => {
    console.log('Student clicked:', student)
    setStudentForReport(student)
  }

  const handleReportDrawerClose = () => {
    setStudentForReport(null)
  }

  const getAssessmentTypeDisplay = type => {
    const typeMap = {
      communication: 'Communication Assessment',
      hinglish: 'Communication Assessment',
      behavior: 'Behavioral Assessment',
      aptitude: 'Cognitive Aptitude',
      role_based: 'Role Based Assessment',
      technical_mcq: 'Technical Assessment - MCQ',
      technical_coding: 'Technical Assessment - Coding',
    }
    const normalizedType = (type || '').toLowerCase().replace(/[\s-]+/g, '_')
    return typeMap[normalizedType] || typeMap[type] || type
  }


  console.log('instituteDetails:', instituteDetails);

  // console.log('instituteDetails.instituteId:', instituteDetails?.instituteId);
  // console.log('instituteDetails.instituteCampusId:', instituteDetails?.instituteCampusId);



  if (!assessment) {
    return (
      <SectionWrapper column>
        <HeaderContainer>
          <TopSection>
            <TopHeader>Subscribed Institute</TopHeader>
            <ButtonContainer>
              <Button.Secondary text="Back" onClick={onClose} />
            </ButtonContainer>
          </TopSection>
        </HeaderContainer>
        <ContentSection>
          <div>Loading assessment details...</div>
        </ContentSection>
      </SectionWrapper>
    )
  }

  const filteredDataForList = useMemo(() => {
    const rawData = studentList || []

    console.log('🔍 [filteredDataForList] Input debug:')
    console.log('  - rawData length:', rawData.length)
    console.log('  - rawData[0]:', rawData[0])
    console.log('  - chartFiltersFromRedux:', chartFiltersFromRedux)

    // If no chart filters at all, return original data
    if (!chartFiltersFromRedux || Object.keys(chartFiltersFromRedux).length === 0) {
      console.log('🔧 [AssessmentDetails] No chart filters, returning original data')
      return rawData
    }

    // Only treat these as concrete filters
    const hasCefr = !!chartFiltersFromRedux.cefrLevel
    const hasApt = !!chartFiltersFromRedux.aptitudeLevel
    const hasCons = !!chartFiltersFromRedux.consistency
    const hasConcreteFilters = hasCefr || hasApt || hasCons
    if (!hasConcreteFilters) {
      return rawData
    }

    // Helper: robust CEFR extraction for filtering
    const ALLOWED_CEFR = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', '-'])
    const normUp = v => (typeof v === 'string' ? v.trim().toUpperCase() : '')
    const extractCefrValues = (cand) => {
      const od = cand?.originalData || {}
      const vals = []
      const pushIf = v => { const n = normUp(v); if (ALLOWED_CEFR.has(n)) vals.push(n) }

      // Check common fields
      pushIf(cand.assignedcefr?.primary); pushIf(cand.assignedcefr?.secondary)
      pushIf(cand.cefrPrimary); pushIf(cand.cefr_primary);
      pushIf(cand.cefrSecondary); pushIf(cand.cefr_secondary);
      pushIf(cand.cefr); pushIf(cand.CEFR); pushIf(cand.cefrLevel);

      // Check originalData
      pushIf(od.assignedcefr?.primary); pushIf(od.assignedcefr?.secondary)
      pushIf(od.cefrPrimary); pushIf(od.cefr_primary);
      pushIf(od.cefr); pushIf(od.CEFR);

      // Check communication scores object
      const comm = cand.communicationScores || cand.commScores || cand.scores || cand.communication
      if (comm && typeof comm === 'object') {
        pushIf(comm.primaryCefr); pushIf(comm.primary);
        pushIf(comm.secondaryCefr); pushIf(comm.secondary);
        pushIf(comm.overallCEFR); pushIf(comm.cefr);
      }

      return Array.from(new Set(vals))
    }

    // Helper: Aptitude extraction for filtering
    const normalizeApt = (v) => {
      if (v == null) return ''
      const s = String(v).trim().toLowerCase()
      if (s.startsWith('begin')) return 'beginner'
      if (s.includes('learn')) return 'learner'
      if (s.startsWith('compet')) return 'competent'
      if (s.startsWith('inter')) return 'intermediate'
      if (s.startsWith('adv')) return 'advanced'
      if (s.startsWith('exp') || s.includes('profic')) return 'expert'
      return s
    }
    const extractAptitudeValues = (cand) => {
      const od = cand?.originalData || {}
      const vals = []
      const pushIf = v => { const n = normalizeApt(v); if (n) vals.push(n) }

      pushIf(cand.aptitudeLevel); pushIf(cand.aptitude_level); pushIf(cand.proficiencyLevel)
      pushIf(cand.currentLevel); pushIf(cand.current_level)  // Check currentLevel for aptitude
      pushIf(od.aptitudeLevel); pushIf(od.aptitude_level);
      pushIf(od.currentLevel); pushIf(od.current_level)  // Also check in originalData

      const scores = cand.aptitudeScores || cand.scores || cand.aptitude
      if (scores && typeof scores === 'object') {
        pushIf(scores.overallLevel); pushIf(scores.level); pushIf(scores.competencyLevel)
      }

      // Fallback: derive from percentage
      const pctRaw = (cand?.aptitudeScores?.overallPercentage ?? od?.aptitudeScores?.overallPercentage ?? cand?.overallPercentage)
      if ((pctRaw || pctRaw === 0) && !Number.isNaN(Number(pctRaw))) {
        const pct = Number(pctRaw)
        vals.push(pct < 25 ? 'beginner' : pct < 50 ? 'learner' : pct < 75 ? 'advanced' : 'expert')
      }

      return Array.from(new Set(vals))
    }

    // Apply filters
    // NOTE: CEFR and AptitudeLevel filtering is now handled SERVER-SIDE in the API call.
    // The API request includes these filters in the payload, so we should NOT re-filter client-side.
    // Only consistency filtering remains as a fallback if needed.
    const filtered = rawData.filter(candidate => {
      // const assessmentTypeLower = (assessment?.assessmentType || '').toLowerCase()
      // const isCommunication = assessmentTypeLower === 'communication'
      // const isAptitude = assessmentTypeLower === 'aptitude'

      // CEFR Filter - DISABLED: Now handled by API
      // if (chartFiltersFromRedux.cefrLevel && isCommunication) {
      //   const target = normUp(chartFiltersFromRedux.cefrLevel).match(/A1|A2|B1|B2|C1|C2|-/)?.[0]
      //   const values = extractCefrValues(candidate)
      //   if (target && !values.includes(target)) return false
      // }

      // Aptitude Level Filter - DISABLED: Now handled by API
      // if (chartFiltersFromRedux.aptitudeLevel && isAptitude) {
      //   const target = normalizeApt(chartFiltersFromRedux.aptitudeLevel)
      //   const values = extractAptitudeValues(candidate)
      //   if (target && !values.includes(target)) return false
      // }

      // Consistency Filter - Keep as fallback (may not be needed if API handles it)
      if (chartFiltersFromRedux.consistency) {
        const candidateConsistency = candidate.consistencyLevel || candidate.consistency_level || candidate.consistency
        if (!candidateConsistency) return true // Allow if no consistency data on candidate
        const filterConsistency = chartFiltersFromRedux.consistency.toLowerCase()
        const match = candidateConsistency?.toLowerCase().includes(filterConsistency) ||
          (filterConsistency === 'high' && candidateConsistency?.toLowerCase().includes('high')) ||
          (filterConsistency === 'moderate' && candidateConsistency?.toLowerCase().includes('moderate')) ||
          (filterConsistency === 'inconsistent' && candidateConsistency?.toLowerCase().includes('inconsistent'))
        if (!match) return false
      }

      return true
    })

    console.log('🔍 [filteredDataForList] Output debug:')
    console.log('  - filtered length:', filtered.length)
    console.log('  - filtered[0]:', filtered[0])

    return filtered
  }, [studentList, chartFiltersFromRedux, assessment])



  // Temporary placeholders for chart data to preventing crashing
  const studentDataCompleted = []
  const studentDataSent = []
  const studentDataPending = []
  const studentDataInProgress = []
  const studentDataDroppedOff = []

  console.log(`[AssessmentDetails] Rendering CandidateList for status: '${currentActiveStatus}'. Data length: ${filteredDataForList?.length || 0}`);
  console.log('🚨 DEBUGGING: AssessmentDetails component is rendering!');

  return (
    <SectionWrapper column>
      <HeaderContainer>
        <TopSection onClick={handleBackClick} style={{ cursor: 'pointer' }}>
          <BackArrow style={{ marginRight: '8px' }} />
          <TopHeader>Schedule List</TopHeader>
        </TopSection>
      </HeaderContainer>

      {/* Schedule Name Header */}
      {assessment && (
        <div style={{
          padding: '12px 16px',
          marginTop: '4px',
          marginBottom: '8px',
          borderBottom: '1px solid #e8e8e8',
        }}>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
            {(assessment.isOneTime || assessment.isStandalone || !isStandardType)
              ? (assessment.scheduleName && assessment.scheduleName !== 'N/A' ? assessment.scheduleName : assessment.name || 'Assessment')
              : isDiagnosisActive
                ? `${assessment.scheduleName}-diagnosis`
                : `${assessment.scheduleName}-schedule-${currentNavIndex || 1}`}
          </span>
          {assessment.assessmentType && (
            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
              ({getAssessmentTypeDisplay(assessment.assessmentType)})
            </span>
          )}
        </div>
      )}

      {navList.length > 1 && (
        <AssessmentNavBar
          assessmentList={navList}
          currentIndex={currentNavIndex}
          onNavigate={(idx) => {
            if (idx === 0 && navList[0]?.isDiagnosis && onSwitchToDiagnosis) {
              onSwitchToDiagnosis()
              return
            }
            setCurrentNavIndex(idx)
            setCurrentPage(1)
          }}
        />
      )}

      {/* Assessment Charts - Only for scheduled communication/aptitude (not one-time) */}
      {assessment && isStandardType && !assessment.isOneTime && !assessment.isStandalone && (
        <div style={{ marginBottom: '20px', padding: '0 8px' }}>
          <AssessmentCharts
            assessment={chartAssessment}
            instituteId={instituteDetails?.instituteId || instituteDetails?.id || ''}
            passingYear={topLevelPassingYear || ''}
            studentDataCompleted={studentDataCompleted}
            studentDataSent={studentDataSent}
            studentDataPending={studentDataPending}
            studentDataInProgress={studentDataInProgress}
            studentDataDroppedOff={studentDataDroppedOff}
            chartFilters={chartFiltersFromRedux}
            setChartFilters={setChartFilters}
            clearChartFilters={clearChartFilters}
            scheduleId={assessment?.scheduleId || null}
            attemptNumber={activeAssessment?.attemptNumber || null}
          />
        </div>
      )}

      <ContentSection>
        <CandidateList
          data={isOtherType
            ? (currentActiveStatus === 'sent'
              ? (otherTypeStudentData?.sent || [])
              : (otherTypeStudentData?.[currentActiveStatus] || []))
            : filteredDataForList}
          loading={isOtherType ? otherTypeDataLoading : studentListLoading}
          totalCount={isOtherType
            ? (currentActiveStatus === 'sent'
              ? (otherTypeStudentData?.sent?.length || 0)
              : (otherTypeStudentData?.[currentActiveStatus]?.length || 0))
            : (studentListPagination?.total || 0)}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onCandidateClick={handleCandidateClick}
          assessmentType={assessment?.assessmentType}
          assessmentId={selectedAssessmentId}
          chartFilters={chartFiltersFromRedux}
          isOtherType={isOtherType}
          otherTypeData={otherTypeStudentData}
          currentActiveStatus={currentActiveStatus}
          onStatusChange={handleStatusClick}
          // Pass down filter props
          topLevelDomain={topLevelDomain}
          setTopLevelDomain={setTopLevelDomain}
          topLevelDegree={topLevelDegree}
          setTopLevelDegree={setTopLevelDegree}
          topLevelDepartment={topLevelDepartment}
          setTopLevelDepartment={setTopLevelDepartment}
          topLevelSpecialization={topLevelSpecialization}
          setTopLevelSpecialization={setTopLevelSpecialization}
          topLevelPassingYear={topLevelPassingYear}
          setTopLevelPassingYear={setTopLevelPassingYear}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          // Pass down options - use assessment-specific options if available
          domainOptionsFromProps={effectiveDomainOptions}
          degreeOptionsFromProps={effectiveDegreeOptions}
          departmentOptionsFromProps={effectiveDepartmentOptions}
          specializationOptionsFromProps={effectiveSpecializationOptions}
          instituteId={instituteDetails?.instituteId || instituteDetails?.id}
          // Sort change callback for server-side sorting
          onSortChange={setSortBy}
          // Schedule info for header display
          scheduleName={assessment?.scheduleName}
          scheduleNumber={assessment?.scheduleNumber}
          // Status filter (multi-select) — hidden on diagnosis and non-standard types
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          showStatusFilter={!isDiagnosisActive && !isOtherType}
        />
      </ContentSection>

      <StudentReportModal
        visible={!!studentForReport}
        onClose={() => setStudentForReport(null)}
        student={studentForReport}
        allowProctoring={true}
        enabledSections={assessment?.enabledSections}
        assessmentType={assessment?.assessmentType}
      />
    </SectionWrapper>
  )
}

export default AssessmentDetails
